import { memo } from 'react';
import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { ISSUE } from '../../../constants/reviewFilters.js';

/**
 * Person cell of the review table: name, email and a profile line (job title,
 * location, who they report to). A non-company email gets an External badge.
 *
 * @param {object} props
 * @param {{ person: Record<string, string>, issues: Array<{ code: string }> }} props.row
 * @param {Map<string, string>} [props.managerNames] Name of each address "Reports to" can point at.
 */
function PersonCell({ row, managerNames }) {
  const { t } = useTranslation('people-access');
  const { person, issues } = row;
  const name = `${person.first_name} ${person.last_name}`.trim() || t('import.review.person.noName');
  const profile = [
    person.job_title,
    person.location,
    person.reports_to
      ? t('import.review.person.reportsTo', { name: managerNames?.get(person.reports_to.trim().toLowerCase()) ?? person.reports_to })
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const external = issues.some((issue) => issue.code === ISSUE.OUTSIDE_DOMAIN);

  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-1">
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-semibold text-content">{name}</span>
        {external ? (
          <Badge size="small" variant="warning">
            {t('import.review.person.external')}
          </Badge>
        ) : null}
      </div>
      <span className="truncate text-xs text-content-secondary">{person.email}</span>
      {profile ? <span className="truncate text-xs text-content-muted">{profile}</span> : null}
    </div>
  );
}

export default memo(PersonCell);
